import { useEffect } from 'react'
import { analytics } from 'segment_analytics'
import { FeatureFlagKey, FeatureFlagAccess } from './types'
import { User } from '@fable/types'
import { ENVIRONMENT } from '../../constants'
import { useFeatureFlags } from './useFeatureFlags'
import { getParameterByName } from '@fable/utils'

export const getFeatureFlagAccess = ({
  name,
  access,
  user,
}: {
  name: FeatureFlagKey
  access: FeatureFlagAccess | undefined
  user: User | null
}) => {
  const ffParam = getParameterByName('ff')
  if (!!ffParam && ffParam === name) return true
  if (access === 'staging') return ENVIRONMENT !== 'production'
  if (access === 'internal') return !!user?.email?.includes('@fable.co')
  return access === 'open'
}

export const useVertExpEnabled = () => {
  const flagEnabled = useFeatureFlags((s) => s.flagEnabled)
  return flagEnabled('vertExp')
}

// Global tracking refs to prevent duplicate tracking across components
const globalTrackingRefs = new Map<FeatureFlagKey, boolean>()

// Helper function to track split tests when flags are used
export const useSplitTestTracking = (
  flagNames: FeatureFlagKey | FeatureFlagKey[]
) => {
  const [flags, config, flagEnabled] = useFeatureFlags((s) => [
    s.flags,
    s.config,
    s.flagEnabled,
  ])

  useEffect(() => {
    // Only trigger if we have flags loaded
    if (!flags.length) return

    // Normalize to array for consistent processing
    const flagNamesArray = Array.isArray(flagNames) ? flagNames : [flagNames]

    flagNamesArray.forEach((flagName) => {
      // Skip if we've already triggered this flag globally
      if (globalTrackingRefs.get(flagName)) return

      const flagConfig = config.find((flag) => flag.name === flagName)

      // Only track if this flag has a split test configured
      if (
        flagConfig?.splitTest &&
        flagConfig.enabled &&
        flagConfig.source === 'server'
      ) {
        // Get the actual flag value (true/false)
        const isFlagEnabled = flagEnabled(flagName)

        analytics.events.splitTestTriggered({
          name: flagConfig.splitTest,
          cohort: isFlagEnabled ? 'test' : 'control',
        })

        // Mark this flag as tracked globally
        globalTrackingRefs.set(flagName, true)
      }
    })
  }, [flags, config, flagEnabled, flagNames])
}
